import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../contexts/ThemeContext';

const VideoLoadingBackground = ({ className = '', progress = 0, isLoading = true }) => {
  const svgRef = useRef();
  const animationRef = useRef();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (!isLoading) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = window.innerWidth;
    const height = window.innerHeight;

    svg.attr('width', width).attr('height', height);

    // Create gradient definitions
    const defs = svg.append('defs');

    // Animated gradient for loading background
    const loadingGradient = defs.append('radialGradient')
      .attr('id', 'loadingGradient')
      .attr('cx', '50%').attr('cy', '50%').attr('r', '70%');

    if (isDarkMode) {
      loadingGradient.selectAll('stop').data([
        { offset: '0%', color: 'rgba(247, 148, 29, 0.1)' }, // Primary color
        { offset: '30%', color: 'rgba(247, 148, 29, 0.05)' },
        { offset: '70%', color: 'rgba(59, 130, 246, 0.05)' }, // Blue accent
        { offset: '100%', color: 'rgba(0, 0, 0, 0.9)' }
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color);
    } else {
      loadingGradient.selectAll('stop').data([
        { offset: '0%', color: 'rgba(247, 148, 29, 0.15)' },
        { offset: '30%', color: 'rgba(247, 148, 29, 0.08)' },
        { offset: '70%', color: 'rgba(59, 130, 246, 0.08)' },
        { offset: '100%', color: 'rgba(255, 255, 255, 0.95)' }
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color);
    }

    // Particle gradient
    const particleGradient = defs.append('radialGradient')
      .attr('id', 'particleGradient')
      .attr('cx', '50%').attr('cy', '50%').attr('r', '50%');

    particleGradient.selectAll('stop').data([
      { offset: '0%', color: isDarkMode ? 'rgba(247, 148, 29, 0.8)' : 'rgba(247, 148, 29, 0.6)' },
      { offset: '100%', color: 'rgba(247, 148, 29, 0)' }
    ]).enter().append('stop')
      .attr('offset', d => d.offset)
      .attr('stop-color', d => d.color);

    // Background
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'url(#loadingGradient)');

    // Create animated elements
    const numWaves = 4;
    const numParticles = Math.floor(width / 30);

    // Animated waves
    const waveData = [];
    for (let i = 0; i < numWaves; i++) {
      waveData.push({
        id: i,
        amplitude: 30 + (i * 15),
        frequency: 0.005 + (i * 0.002),
        phase: (i * Math.PI) / 2,
        speed: 1 + (i * 0.3),
        opacity: 0.3 - (i * 0.05)
      });
    }

    const line = d3.line()
      .x(d => d.x)
      .y(d => d.y)
      .curve(d3.curveBasis);

    const waveGroup = svg.append('g').attr('class', 'waves');

    waveData.forEach(wave => {
      waveGroup.append('path')
        .attr('class', `wave-${wave.id}`)
        .attr('fill', 'none')
        .attr('stroke', isDarkMode ? 'rgba(247, 148, 29, 0.3)' : 'rgba(247, 148, 29, 0.4)')
        .attr('stroke-width', 2 + wave.id)
        .attr('opacity', wave.opacity);
    });

    // Progress ring
    const centerX = width / 2;
    const centerY = height / 2;
    const ringRadius = 60;

    const progressGroup = svg.append('g')
      .attr('transform', `translate(${centerX}, ${centerY})`);

    // Background ring
    progressGroup.append('circle')
      .attr('r', ringRadius)
      .attr('fill', 'none')
      .attr('stroke', isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
      .attr('stroke-width', 4);

    // Progress arc
    const arc = d3.arc()
      .innerRadius(ringRadius - 2)
      .outerRadius(ringRadius + 2)
      .startAngle(0);

    const progressArc = progressGroup.append('path')
      .attr('fill', 'url(#particleGradient)')
      .attr('stroke', 'rgba(247, 148, 29, 0.8)')
      .attr('stroke-width', 1);

    // Center loading text
    const textGroup = progressGroup.append('g');

    textGroup.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '-0.3em')
      .attr('fill', isDarkMode ? '#f7941d' : '#f7941d')
      .attr('font-size', '18px')
      .attr('font-weight', 'bold')
      .text('Loading');

    const progressText = textGroup.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '1.2em')
      .attr('fill', isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)')
      .attr('font-size', '14px');

    // Floating particles
    const particles = [];
    for (let i = 0; i < numParticles; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 3 + 1,
        opacity: Math.random() * 0.6 + 0.2,
        life: Math.random() * 100
      });
    }

    const particleGroup = svg.append('g').attr('class', 'particles');

    const particleElements = particleGroup.selectAll('circle')
      .data(particles)
      .enter()
      .append('circle')
      .attr('fill', 'url(#particleGradient)')
      .attr('r', d => d.radius);

    // Animation loop
    let time = 0;
    const animate = () => {
      time += 0.02;

      // Update waves
      waveData.forEach(wave => {
        const points = [];
        for (let x = 0; x <= width; x += 8) {
          const y = height / 2 +
            Math.sin(x * wave.frequency + time * wave.speed + wave.phase) * wave.amplitude +
            Math.sin(x * wave.frequency * 2 + time * wave.speed * 1.5) * (wave.amplitude * 0.3);
          points.push({ x, y });
        }

        waveGroup.select(`.wave-${wave.id}`)
          .datum(points)
          .attr('d', line);
      });

      // Update progress arc
      const progressAngle = (progress / 100) * 2 * Math.PI;
      progressArc.attr('d', arc.endAngle(progressAngle));

      // Update progress text
      progressText.text(`${Math.round(progress)}%`);

      // Update particles
      particles.forEach(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life += 1;

        // Wrap around edges
        if (particle.x < 0) particle.x = width;
        if (particle.x > width) particle.x = 0;
        if (particle.y < 0) particle.y = height;
        if (particle.y > height) particle.y = 0;

        // Fade and regenerate
        if (particle.life > 200) {
          particle.life = 0;
          particle.opacity = Math.random() * 0.6 + 0.2;
          particle.x = Math.random() * width;
          particle.y = Math.random() * height;
        }

        const fadeOpacity = particle.life < 50 ? particle.life / 50 :
                           particle.life > 150 ? (200 - particle.life) / 50 : 1;
        particle.currentOpacity = particle.opacity * fadeOpacity;
      });

      particleElements
        .attr('cx', d => d.x)
        .attr('cy', d => d.y)
        .attr('opacity', d => d.currentOpacity);

      if (isLoading) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animate();

    // Handle resize
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      svg.attr('width', newWidth).attr('height', newHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDarkMode, isLoading, progress]);

  if (!isLoading) return null;

  return (
    <div className={`fixed inset-0 ${className}`} style={{ zIndex: 1 }}>
      <svg
        ref={svgRef}
        className="w-full h-full"
        style={{ mixBlendMode: isDarkMode ? 'screen' : 'multiply' }}
      />
    </div>
  );
};

export default VideoLoadingBackground;